import {
  PracticeFormQuestion,
  PracticePatientFormData,
} from "@physiofit-digital/physiofit_types";
import * as Sentry from "@sentry/react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { useEffect } from "react";
import {
  getCurrentPracticeFormIndex,
  getPracticeFormId,
  getPracticeFormIdsArray,
  getRunStartFormIndex,
} from "../../../core/store/currentFormSlice";
import { useMainStore } from "../../../core/store/mainStore";
import { db } from "../../../firebaseConfig";
import { restoredFormIsCurrent } from "./restoredForm";

const formRequestsAddress = (
  form: Pick<PracticePatientFormData, "formType" | "requestAddress">,
) => form.requestAddress ?? form.formType === "onboarding";

// The personal-data step is asked once per run, on its first form, so the
// address has to be requested there whenever a later form of the link wants it.
const otherLinkFormsRequestAddress = async (otherFormIds: string[]) => {
  const snapshots = await Promise.all(
    otherFormIds.map((id) => getDoc(doc(db, "practiceForms/" + id))),
  );
  return snapshots.some(
    (snapshot) =>
      snapshot.exists() &&
      formRequestsAddress(snapshot.data() as PracticePatientFormData),
  );
};

export const usePracticeFormConnect = () => {
  const practiceFormId = useMainStore(getPracticeFormId);
  const practiceFormIdsArray = useMainStore(getPracticeFormIdsArray);
  const currentPracticeFormIndex = useMainStore(getCurrentPracticeFormIndex);
  const runStartFormIndex = useMainStore(getRunStartFormIndex);
  const setCurrentPracticePatientForm = useMainStore(
    (state) => state.setCurrentPracticePatientForm,
  );

  useEffect(() => {
    if (!practiceFormId) {
      setCurrentPracticePatientForm(undefined);
      return;
    }
    const isFirstForm = currentPracticeFormIndex === runStartFormIndex;
    let cancelled = false;
    let formInstalled = false;
    let unsubscribe: (() => void) | undefined;

    const subscribe = async () => {
      let othersRequestAddress = false;
      if (isFirstForm) {
        const otherFormIds = (practiceFormIdsArray ?? [])
          .map((form) => form.id)
          .filter((id) => id !== practiceFormId);
        try {
          othersRequestAddress =
            await otherLinkFormsRequestAddress(otherFormIds);
        } catch (error) {
          // Losing the address for this session is better than losing the form.
          console.error("Reading the link's other forms failed:", error);
          Sentry.captureException(error);
        }
      }
      if (cancelled) return;

      unsubscribe = onSnapshot(
        doc(db, "practiceForms/" + practiceFormId),
        (snapshot) => {
          if (snapshot.exists()) {
            const practiceForm = snapshot.data() as PracticePatientFormData;
            const practicePatientForm = buildPracticePatientForm(
              practiceForm,
              isFirstForm,
              othersRequestAddress,
            );
            // Only the first snapshot installs the form, and not even that one
            // when a reload restored this form unedited from the session. A later one carries a
            // practice-side edit, and re-setting would discard the answers the
            // patient has already given and clear the record of completed
            // writes mid-run. A new position in the link resubscribes, so the
            // next form of a chain still installs.
            if (formInstalled) {
              return;
            }
            formInstalled = true;
            if (restoredFormIsCurrent(useMainStore.getState().currentPracticePatientForm, practiceForm)) {
              return;
            }
            setCurrentPracticePatientForm(practicePatientForm);
          } else {
            setCurrentPracticePatientForm(undefined);
          }
        },
        (error) => {
          // InviteLayout mounts this pre-login, so a denial here is expected and
          // self-healing (FormLayout re-subscribes after sign-in). Without the
          // callback the listener died as an unhandled error.
          console.error("Practice form listener failed:", error);
          setCurrentPracticePatientForm(undefined);
        },
      );
    };
    subscribe();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [practiceFormId, practiceFormIdsArray, currentPracticeFormIndex, runStartFormIndex]);
};

const buildPersonalInfoQuestion = (
  formType: string,
  includeAddress: boolean,
): PracticeFormQuestion => {
  return {
    id: `${formType}-personal-info`,
    questionIndex: -1,
    question: {
      de: "Persönliche Daten eingeben",
      en: "Enter personal information",
      tr: "Kişisel Verileri Girin",
    },
    answer: {
      answerId: `${formType}-personal-info-answer`,
      type: "personalInfo",
      ...(includeAddress ? {} : { options: ["noAddress"] }),
    },
    required: true,
  } as PracticeFormQuestion;
};

const buildPracticePatientForm = (
  form: PracticePatientFormData | undefined,
  isFirstForm: boolean,
  othersRequestAddress: boolean,
) => {
  if (!form) return undefined;
  if (!isFirstForm) return form;

  const formType = form.formType;
  const includeAddress = formRequestsAddress(form) || othersRequestAddress;
  return {
    ...form,
    questions: {
      ...(form?.questions || {}),
      [`${formType}-personal-info`]: buildPersonalInfoQuestion(
        formType,
        includeAddress,
      ),
    },
  };
};
